import PropTypes from 'prop-types';
import { X, SlidersHorizontal } from 'lucide-react';
import { Heading, Text, Stack } from '@/components/ui/index.js';
import styles from './FilterSheet.module.css';

// Filter options
const sportOptions = [
  { value: 'basketball', label: 'Basketball', icon: '🏀' },
  { value: 'soccer', label: 'Soccer', icon: '⚽' },
  { value: 'tennis', label: 'Tennis', icon: '🎾' },
  { value: 'running', label: 'Running', icon: '🏃' },
  { value: 'swimming', label: 'Swimming', icon: '🏊' },
  { value: 'cycling', label: 'Cycling', icon: '🚴' },
  { value: 'volleyball', label: 'Volleyball', icon: '🏐' },
  { value: 'yoga', label: 'Yoga', icon: '🧘' },
];

const skillLevelOptions = [
  { value: 'beginner', label: 'Beginner', color: 'lime' },
  { value: 'intermediate', label: 'Intermediate', color: 'cyan' },
  { value: 'advanced', label: 'Advanced', color: 'warning' },
];

const dateRangeOptions = [
  { value: 'today', label: 'Today' },
  { value: 'this-week', label: 'This Week' },
  { value: 'this-weekend', label: 'This Weekend' },
  { value: 'next-week', label: 'Next Week' },
];

const timeOfDayOptions = [
  { value: 'morning', label: 'Morning (6AM - 12PM)' },
  { value: 'afternoon', label: 'Afternoon (12PM - 5PM)' },
  { value: 'evening', label: 'Evening (5PM - 10PM)' },
];

/**
 * FilterSheet - Bottom sheet modal for event filtering
 * Contains sport, skill level, date, distance, and time filters
 */
export function FilterSheet({
  isOpen,
  onClose,
  filters,
  onToggleSport,
  onToggleSkillLevel,
  onSetDateRange,
  onSetMaxDistance,
  onToggleTimeOfDay,
  onClearAll,
  onApply,
}) {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />

      {/* Sheet */}
      <div className={styles.sheet} role="dialog" aria-label="Filter events">
        {/* Handle */}
        <div className={styles.handle} />

        {/* Header */}
        <div className={styles.header}>
          <Stack direction="row" gap={2} align="center">
            <SlidersHorizontal size={20} className={styles.headerIcon} />
            <Heading level={2} size="md" color="primary">
              Filters
            </Heading>
          </Stack>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close filters"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {/* Sport filter */}
          <section className={styles.section}>
            <Heading level={3} size="sm" color="secondary" className={styles.sectionTitle}>
              Sport Type
            </Heading>
            <div className={styles.chipGrid}>
              {sportOptions.map((sport) => (
                <button
                  key={sport.value}
                  type="button"
                  className={`${styles.chip} ${
                    filters.sports.includes(sport.value) ? styles.chipActive : ''
                  }`}
                  onClick={() => onToggleSport(sport.value)}
                >
                  <span className={styles.chipIcon}>{sport.icon}</span>
                  <Text size="sm" color="inherit">
                    {sport.label}
                  </Text>
                </button>
              ))}
            </div>
          </section>

          {/* Date range filter */}
          <section className={styles.section}>
            <Heading level={3} size="sm" color="secondary" className={styles.sectionTitle}>
              When
            </Heading>
            <div className={styles.chipGrid}>
              {dateRangeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`${styles.chip} ${
                    filters.dateRange === option.value ? styles.chipActive : ''
                  }`}
                  onClick={() => onSetDateRange(option.value)}
                >
                  <Text size="sm" color="inherit">
                    {option.label}
                  </Text>
                </button>
              ))}
            </div>
          </section>

          {/* Skill level filter */}
          <section className={styles.section}>
            <Heading level={3} size="sm" color="secondary" className={styles.sectionTitle}>
              Skill Level
            </Heading>
            <div className={styles.checkboxGroup}>
              {skillLevelOptions.map((level) => (
                <label key={level.value} className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={filters.skillLevels.includes(level.value)}
                    onChange={() => onToggleSkillLevel(level.value)}
                  />
                  <span className={`${styles.checkmark} ${styles[`checkmark${level.color}`]}`} />
                  <Text size="sm" color="secondary">
                    {level.label}
                  </Text>
                </label>
              ))}
            </div>
          </section>

          {/* Distance filter */}
          <section className={styles.section}>
            <Heading level={3} size="sm" color="secondary" className={styles.sectionTitle}>
              Max Distance
            </Heading>
            <div className={styles.sliderContainer}>
              <input
                type="range"
                min="1"
                max="25"
                value={filters.maxDistance}
                onChange={(e) => onSetMaxDistance(Number(e.target.value))}
                className={styles.slider}
              />
              <Text size="sm" color="primary" align="center">
                {filters.maxDistance} miles
              </Text>
            </div>
          </section>

          {/* Time of day filter */}
          <section className={styles.section}>
            <Heading level={3} size="sm" color="secondary" className={styles.sectionTitle}>
              Time of Day
            </Heading>
            <div className={styles.checkboxGroup}>
              {timeOfDayOptions.map((time) => (
                <label key={time.value} className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={filters.timeOfDay.includes(time.value)}
                    onChange={() => onToggleTimeOfDay(time.value)}
                  />
                  <span className={styles.checkmark} />
                  <Text size="sm" color="secondary">
                    {time.label}
                  </Text>
                </label>
              ))}
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button type="button" className={styles.clearButton} onClick={onClearAll}>
            Clear All
          </button>
          <button type="button" className={styles.applyButton} onClick={onApply}>
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
}

FilterSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  filters: PropTypes.shape({
    sports: PropTypes.arrayOf(PropTypes.string).isRequired,
    skillLevels: PropTypes.arrayOf(PropTypes.string).isRequired,
    dateRange: PropTypes.string,
    maxDistance: PropTypes.number.isRequired,
    timeOfDay: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  onToggleSport: PropTypes.func.isRequired,
  onToggleSkillLevel: PropTypes.func.isRequired,
  onSetDateRange: PropTypes.func.isRequired,
  onSetMaxDistance: PropTypes.func.isRequired,
  onToggleTimeOfDay: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};

export default FilterSheet;
